/**
 * Template maturity map helpers (mirrors backend maturityMap rules).
 * Bands are % of scale maximum (score ÷ scale_max); half-open [min, max) except last band closed.
 */

export interface AeMaturityBand {
  id: string;
  label: string;
  min_pct: number;
  max_pct: number;
  sort_order: number;
}

export interface AeMaturityMapConfig {
  enabled: boolean;
  bands: AeMaturityBand[];
}

export interface AeMaturityLevel {
  pct: number | null;
  /** Item-equal mean of raw scores. */
  mean_score?: number | null;
  band_id: string | null;
  label: string | null;
}

export interface AeMaturityPayload {
  enabled: true;
  bands: AeMaturityBand[];
  overall: AeMaturityLevel;
  sections: Array<{
    section_id: string;
    section_code: string;
    section_name: string;
  } & AeMaturityLevel>;
  benchmark_b1: {
    overall: AeMaturityLevel;
    sections: Array<{ section_id: string } & AeMaturityLevel>;
  } | null;
  model: {
    band_id: string;
    label: string;
    min_pct: number;
    max_pct: number;
  };
  footnote: string;
}

export function newMaturityBandId(): string {
  return `band_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function defaultMaturityMap(): AeMaturityMapConfig {
  return {
    enabled: false,
    bands: [
      {
        id: newMaturityBandId(),
        label: '',
        min_pct: 0,
        max_pct: 100,
        sort_order: 0,
      },
    ],
  };
}

export function parseMaturityMap(raw: unknown): AeMaturityMapConfig {
  if (!raw || typeof raw !== 'object') return defaultMaturityMap();
  const obj = raw as Record<string, unknown>;
  const enabled = obj.enabled === true;
  const bandsRaw = Array.isArray(obj.bands) ? obj.bands : [];
  const bands: AeMaturityBand[] = bandsRaw
    .map((b, i) => {
      if (!b || typeof b !== 'object') return null;
      const row = b as Record<string, unknown>;
      const label = typeof row.label === 'string' ? row.label : '';
      const min = Number(row.min_pct);
      const max = Number(row.max_pct);
      const id =
        typeof row.id === 'string' && row.id.trim() ? row.id.trim() : newMaturityBandId();
      const sortOrder = Number(row.sort_order);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
      return {
        id,
        label,
        min_pct: min,
        max_pct: max,
        sort_order: Number.isFinite(sortOrder) ? sortOrder : i,
      };
    })
    .filter((b): b is AeMaturityBand => b != null);

  return {
    enabled,
    bands: bands.length ? bands : defaultMaturityMap().bands,
  };
}

export function sortMaturityBands(bands: AeMaturityBand[]): AeMaturityBand[] {
  return [...bands].sort((a, b) => {
    if (a.min_pct !== b.min_pct) return a.min_pct - b.min_pct;
    return a.sort_order - b.sort_order;
  });
}

export interface MaturityValidationIssue {
  code: string;
  messageKey: string;
}

/** Client-side validation aligned with backend publish rules. */
export function validateMaturityMapClient(
  config: AeMaturityMapConfig
): MaturityValidationIssue[] {
  const issues: MaturityValidationIssue[] = [];
  if (!config.enabled) return issues;
  if (!config.bands.length) {
    issues.push({ code: 'MATURITY_BANDS_REQUIRED', messageKey: 'assessmentEngine.builder.maturityBandsRequired' });
    return issues;
  }
  for (const band of config.bands) {
    if (!band.label.trim()) {
      issues.push({ code: 'MATURITY_LABEL', messageKey: 'assessmentEngine.builder.maturityLabelRequired' });
    }
    if (!(band.min_pct >= 0 && band.max_pct <= 100)) {
      issues.push({ code: 'MATURITY_RANGE', messageKey: 'assessmentEngine.builder.maturityRangeInvalid' });
    }
    if (!(band.min_pct < band.max_pct)) {
      issues.push({ code: 'MATURITY_MIN_MAX', messageKey: 'assessmentEngine.builder.maturityMinMaxInvalid' });
    }
  }
  const sorted = sortMaturityBands(config.bands);
  if (sorted[0]!.min_pct !== 0) {
    issues.push({ code: 'MATURITY_START', messageKey: 'assessmentEngine.builder.maturityMustStartAtZero' });
  }
  if (sorted[sorted.length - 1]!.max_pct !== 100) {
    issues.push({ code: 'MATURITY_END', messageKey: 'assessmentEngine.builder.maturityMustEndAtHundred' });
  }
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i]!.max_pct !== sorted[i + 1]!.min_pct) {
      issues.push({
        code: 'MATURITY_CONTIGUOUS',
        messageKey: 'assessmentEngine.builder.maturityMustBeContiguous',
      });
      break;
    }
  }
  return issues;
}

/** After editing max of band i, snap next band's min to keep edges contiguous. */
export function syncBandEdges(bands: AeMaturityBand[]): AeMaturityBand[] {
  const sorted = sortMaturityBands(bands).map((b, i) => ({ ...b, sort_order: i }));
  if (!sorted.length) return sorted;
  sorted[0] = { ...sorted[0]!, min_pct: 0 };
  for (let i = 0; i < sorted.length - 1; i++) {
    const cur = sorted[i]!;
    const next = sorted[i + 1]!;
    sorted[i + 1] = { ...next, min_pct: cur.max_pct };
  }
  sorted[sorted.length - 1] = {
    ...sorted[sorted.length - 1]!,
    max_pct: 100,
  };
  return sorted;
}
